import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Loader2, Clock, Users, ChefHat, Wine, Beer, Sparkles,
  Bookmark, BookmarkCheck, MapPin, ArrowLeft, Sprout, RefreshCw,
  Minus, Plus, Share2, Replace,
} from "lucide-react";
import { scaleQuantity } from "../lib/scaleQuantity";
import SubstituteDialog from "../components/SubstituteDialog";
import CookPhotos from "../components/CookPhotos";

const pairingIcon = (type) => {
  if (type === "wine") return Wine;
  if (type === "beer" || type === "cider") return Beer;
  return Sparkles;
};
const pairingLabel = (type) => (type === "non_alcoholic" ? "Zero-Proof" : type);

// Renders chef_tip text with tiny markdown-link support: [label](/internal-path).
// Only internal paths (starting with "/") become <Link>s — anything else falls through as text.
const LINK_RE = /\[([^\]]+)\]\((\/[^)]+)\)/g;
const renderTip = (text) => {
  if (!text || typeof text !== "string") return text;
  const nodes = [];
  let last = 0;
  let m;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <Link
        key={`${m.index}-${m[2]}`}
        to={m[2]}
        data-testid="chef-tip-link"
        className="not-italic text-orange-500 hover:text-orange-400 ink-link"
      >
        {m[1]}
      </Link>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length ? nodes : text;
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const RecipeDetail = () => {
  const { id } = useParams();
  const { user, favoriteIds, toggleFavorite } = useAuth();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [servingsFactor, setServingsFactor] = useState(1);
  const [subIngredient, setSubIngredient] = useState(null);
  const [subOpen, setSubOpen] = useState(false);
  const [veganSwap, setVeganSwap] = useState(null);
  const pollingRef = useRef(null);
  const isFav = recipe ? favoriteIds.has(recipe.id) : false;

  useEffect(() => {
    setLoading(true);
    api
      .get(`/recipes/${id}`)
      .then((res) => setRecipe(res.data))
      .catch(() => setRecipe(null))
      .finally(() => setLoading(false));
    // Reset & fetch the vegan side-swap for this recipe (null if already vegan)
    setVeganSwap(null);
    api
      .get(`/recipes/${id}/vegan_swap`)
      .then((res) => setVeganSwap(res.data || null))
      .catch(() => setVeganSwap(null));
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [id]);

  // Poll for image if pending
  useEffect(() => {
    if (!recipe) return;
    if (recipe.image_status !== "pending") return;
    if (pollingRef.current) clearInterval(pollingRef.current);
    let attempts = 0;
    pollingRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const res = await api.get(`/recipes/${recipe.id}/image_status`);
        if (res.data.status === "ready" && res.data.image_data) {
          setRecipe((r) => ({ ...r, image_data: res.data.image_data, image_status: "ready" }));
          clearInterval(pollingRef.current);
        } else if (res.data.status === "failed" || attempts > 30) {
          setRecipe((r) => ({ ...r, image_status: "failed" }));
          clearInterval(pollingRef.current);
        }
      } catch {
        if (attempts > 30) clearInterval(pollingRef.current);
      }
    }, 4000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [recipe?.id, recipe?.image_status]);

  const handleFav = async () => {
    if (!user) { toast.error("Sign in to save recipes"); return; }
    try {
      const nowFav = await toggleFavorite(recipe.id);
      toast.success(nowFav ? "Popped in your cookbook" : "Whisked out of the cookbook");
    } catch { toast.error("Could not update favorite"); }
  };

  const handleRegenerateImage = async () => {
    try {
      await api.post(`/recipes/${recipe.id}/regenerate_image`);
      setRecipe((r) => ({ ...r, image_data: null, image_status: "pending" }));
      toast.success("Back to the darkroom");
    } catch { toast.error("Could not restart the photograph"); }
  };

  const handleShare = async () => {
    const shareUrl = `${BACKEND}/api/recipes/${recipe.id}/share`;
    const shareData = {
      title: recipe.title,
      text: recipe.subtitle || recipe.story?.slice(0, 140),
      url: shareUrl,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied");
    } catch { toast.error("Could not copy link"); }
  };

  const openSubstitute = (ing) => {
    setSubIngredient(ing);
    setSubOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950 pt-24 px-6">
        <div className="text-center">
          <div className="magazine-number text-9xl mb-6">Ø</div>
          <p className="font-serif-display italic text-2xl text-stone-400 mb-8">
            This one&apos;s gone off the menu.
          </p>
          <Link to="/library" data-testid="detail-back-library" className="text-orange-500 uppercase text-xs tracking-[0.24em] ink-link">
            Back to the library
          </Link>
        </div>
      </div>
    );
  }

  const heroImg = recipe.image_data ? `data:image/png;base64,${recipe.image_data}` : null;
  const imagePending = recipe.image_status === "pending";
  const imageFailed = recipe.image_status === "failed";

  const baseServings = recipe.servings || 1;
  const currentServings = Math.max(1, Math.round(baseServings * servingsFactor));
  const decServings = () => setServingsFactor((f) => Math.max(0.25, (Math.round(baseServings * f) - 1) / baseServings));
  const incServings = () => setServingsFactor((f) => Math.min(8, (Math.round(baseServings * f) + 1) / baseServings));

  return (
    <div className="min-h-screen pt-20 sm:pt-24 pb-20 sm:pb-32 bg-stone-950">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 md:px-10">
        <Link to="/library" data-testid="detail-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-stone-500 hover:text-orange-500 mb-8 sm:mb-10 ink-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Library
        </Link>

        {/* Header */}
        <header className="grid lg:grid-cols-12 gap-8 sm:gap-10 lg:gap-16 mb-16 sm:mb-24">
          <div className="lg:col-span-6 xl:col-span-5">
            <div className="flex items-center gap-4 mb-6 flex-wrap">
              <span className="eyebrow">{recipe.course}</span>
              <span className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
              <span className="eyebrow text-stone-500">{recipe.season}</span>
              <span className="w-1.5 h-1.5 bg-orange-600 rounded-full" />
              <span className="eyebrow text-stone-500">
                {recipe.source === "community" ? "From the readers" : "From the kitchen"}
              </span>
              {recipe.is_vegan && (
                <span
                  data-testid="detail-vegan-badge"
                  className="bg-lime-500 text-stone-950 px-3 py-1 text-[10px] uppercase tracking-[0.24em] font-medium"
                >
                  Fully vegan
                </span>
              )}
            </div>
            <h1 data-testid="detail-title" className="font-serif-display text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[0.95] sm:leading-[0.9] tracking-tight sm:tracking-tighter text-stone-100 break-words">
              {recipe.title}
            </h1>
            {recipe.subtitle && (
              <p className="mt-5 sm:mt-6 font-serif-display italic text-xl sm:text-2xl text-orange-500 leading-snug">{recipe.subtitle}</p>
            )}
            <p className="mt-10 text-stone-300 text-lg leading-relaxed">{recipe.story}</p>

            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-stone-400 font-mono-accent">
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4" />{recipe.region}</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{recipe.prep_time_min + recipe.cook_time_min}m</span>
              <span className="flex items-center gap-2"><ChefHat className="w-4 h-4" />{recipe.difficulty}</span>
            </div>

            {/* Nutrition strip + allergens */}
            {(recipe.nutrition || (recipe.allergens && recipe.allergens.length > 0)) && (
              <div className="mt-8 border border-stone-800 bg-stone-900/40 p-5" data-testid="nutrition-block">
                {recipe.nutrition && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pb-4 border-b border-stone-800">
                    {[
                      { label: "Calories", val: recipe.nutrition.calories, unit: "kcal", color: "text-orange-500" },
                      { label: "Protein", val: recipe.nutrition.protein_g, unit: "g", color: "text-yellow-400" },
                      { label: "Carbs", val: recipe.nutrition.carbs_g, unit: "g", color: "text-lime-400" },
                      { label: "Fat", val: recipe.nutrition.fat_g, unit: "g", color: "text-orange-400" },
                    ].map((n) => (
                      <div key={n.label} className="text-center" data-testid={`nutrition-${n.label.toLowerCase()}`}>
                        <div className={`font-serif-display text-2xl md:text-3xl ${n.color} leading-none`}>
                          {n.val != null ? Math.round(Number(n.val) * (servingsFactor || 1)) : "—"}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1.5">
                          {n.label} <span className="text-stone-600">{n.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {recipe.allergens && recipe.allergens.length > 0 && (
                  <div className="pt-4 flex flex-wrap items-center gap-2">
                    <span className="eyebrow text-stone-500 mr-1">Contains</span>
                    {recipe.allergens.map((a) => (
                      <span
                        key={a}
                        data-testid={`allergen-${a}`}
                        className="text-[10px] uppercase tracking-widest px-2.5 py-1 bg-orange-950/40 border border-orange-800/60 text-orange-400"
                      >
                        {a}
                      </span>
                    ))}
                  </div>
                )}
                {recipe.nutrition && (
                  <div className="pt-3 text-[10px] uppercase tracking-widest text-stone-600 font-mono-accent">
                    Per serving &middot; best-effort estimate
                  </div>
                )}
              </div>
            )}

            <div className="mt-10 flex items-center gap-3 flex-wrap">
              <button onClick={handleFav} data-testid="detail-favorite" className="inline-flex items-center gap-2 bg-orange-700 hover:bg-orange-800 text-stone-50 px-5 py-3 uppercase tracking-[0.2em] text-xs font-medium transition-colors">
                {isFav ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                {isFav ? "In your cookbook" : "Pop in cookbook"}
              </button>
              <button onClick={handleShare} data-testid="detail-share" className="inline-flex items-center gap-2 border border-stone-700 hover:border-orange-600 text-stone-200 hover:text-orange-500 px-5 py-3 uppercase tracking-[0.2em] text-xs font-medium transition-colors">
                <Share2 className="w-4 h-4" /> Share
              </button>
              <span className="text-xs text-stone-500 font-mono-accent">by {recipe.author_name}</span>
            </div>
          </div>

          <div className="lg:col-span-6 xl:col-span-7 relative group">
            {heroImg ? (
              <img src={heroImg} alt={recipe.title} data-testid="detail-hero-image" className="w-full aspect-[4/3] object-cover" />
            ) : imagePending ? (
              <div className="w-full aspect-[4/3] bg-stone-900 border border-stone-800 flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
                <p className="font-serif-display italic text-xl text-stone-400">Plating up the photograph...</p>
              </div>
            ) : imageFailed ? (
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-stone-900 to-stone-950 border border-stone-800 flex flex-col items-center justify-center gap-3">
                <span className="text-stone-500 text-sm font-mono-accent uppercase tracking-widest">Photograph unavailable</span>
              </div>
            ) : (
              <div className="w-full aspect-[4/3] bg-gradient-to-br from-orange-950 via-stone-900 to-stone-950 border border-stone-800 flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-6 h-6 animate-spin text-orange-600/60" />
                <span className="font-serif-display italic text-lg text-stone-400">Photograph on its way</span>
              </div>
            )}
            {!imagePending && (
              <button type="button" onClick={handleRegenerateImage} data-testid="regenerate-image" title="Retake the photograph"
                className="absolute bottom-4 right-4 inline-flex items-center gap-2 bg-stone-950/90 backdrop-blur-md border border-stone-700 hover:border-orange-600 text-stone-200 hover:text-orange-500 px-4 py-2.5 text-[10px] uppercase tracking-[0.24em] transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100">
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
                {heroImg ? "Retake photo" : "Try again"}
              </button>
            )}
          </div>
        </header>

        {/* Body */}
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Ingredients + servings scaler */}
          <aside className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
            <div className="border-t border-stone-800 pt-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Sprout className="w-4 h-4 text-orange-600" />
                  <h2 className="eyebrow">The Shopping List</h2>
                </div>
              </div>

              {/* Servings stepper */}
              <div className="mb-8 border border-stone-800 bg-stone-900/40">
                <div className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-stone-400 uppercase tracking-widest">
                    <Users className="w-3.5 h-3.5 text-orange-600" strokeWidth={1.5} />
                    Servings
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      data-testid="servings-decrement"
                      onClick={decServings}
                      disabled={currentServings <= 1}
                      className="w-8 h-8 border border-stone-800 text-stone-300 hover:border-orange-600 hover:text-orange-500 disabled:opacity-40 disabled:hover:border-stone-800 disabled:hover:text-stone-300 transition-colors flex items-center justify-center"
                      aria-label="Fewer servings"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span data-testid="servings-count" className="font-serif-display text-2xl text-stone-100 w-8 text-center tabular-nums">
                      {currentServings}
                    </span>
                    <button
                      type="button"
                      data-testid="servings-increment"
                      onClick={incServings}
                      className="w-8 h-8 border border-stone-800 text-stone-300 hover:border-orange-600 hover:text-orange-500 transition-colors flex items-center justify-center"
                      aria-label="More servings"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {Math.abs(servingsFactor - 1) > 0.01 && (
                  <div className="px-4 pb-3 text-[10px] uppercase tracking-widest text-stone-500 font-mono-accent">
                    Scaled from {baseServings}. Tap any ingredient for a swap.
                  </div>
                )}
              </div>

              <ul className="space-y-6">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i} data-testid={`ingredient-${i}`} className="pb-6 border-b border-stone-800/60 last:border-0 group/ing">
                    <button
                      type="button"
                      onClick={() => openSubstitute(ing)}
                      data-testid={`ingredient-swap-${i}`}
                      className="w-full text-left transition-colors"
                    >
                      <div className="flex items-baseline justify-between gap-4 mb-1">
                        <span className="font-serif-display text-xl text-stone-100 group-hover/ing:text-orange-500 transition-colors">
                          {ing.name}
                        </span>
                        <span className="text-xs text-orange-500 font-mono-accent whitespace-nowrap">
                          {scaleQuantity(ing.quantity, servingsFactor)}
                        </span>
                      </div>
                      {ing.seasonal_note && (
                        <p className="text-sm text-stone-400 italic leading-relaxed mt-2">{ing.seasonal_note}</p>
                      )}
                      {ing.sourcing_tip && (
                        <p className="text-xs text-stone-500 mt-2 uppercase tracking-wider">
                          <span className="text-orange-600">Where</span> &middot; {ing.sourcing_tip}
                        </p>
                      )}
                      <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-stone-600 group-hover/ing:text-orange-500 transition-colors">
                        <Replace className="w-3 h-3" strokeWidth={1.5} />
                        Swap for what you&apos;ve got
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Steps + Notes */}
          <div className="lg:col-span-8 space-y-16">
            <section>
              <div className="flex items-center gap-3 mb-10 border-t border-stone-800 pt-8">
                <ChefHat className="w-4 h-4 text-orange-600" />
                <h2 className="eyebrow">How to Cook It</h2>
              </div>
              <ol className="space-y-14">
                {recipe.steps.map((step, i) => (
                  <li key={i} data-testid={`step-${i}`} className="grid grid-cols-[80px_1fr] gap-6 sm:gap-10">
                    <div className="magazine-number text-6xl sm:text-7xl">{String(step.number).padStart(2, "0")}</div>
                    <div>
                      <h3 className="font-serif-display text-2xl text-stone-100 mb-3">{step.title}</h3>
                      <p className="text-stone-300 leading-relaxed text-lg">{step.instruction}</p>
                      {step.duration_min ? (
                        <div className="mt-3 text-xs text-stone-500 font-mono-accent uppercase tracking-widest">
                          ~{step.duration_min} min
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {recipe.plating_notes && (
              <section className="bg-stone-900/40 border border-stone-800 p-8 md:p-12">
                <div className="eyebrow mb-4">On the Plate</div>
                <p className="font-serif-display text-2xl md:text-3xl italic text-stone-100 leading-snug">
                  {`\u201C${recipe.plating_notes}\u201D`}
                </p>
              </section>
            )}

            {recipe.tasting_notes && (
              <section>
                <div className="eyebrow mb-6 pt-8 border-t border-stone-800">What It Tastes Of</div>
                <p className="text-stone-300 leading-relaxed text-lg">{recipe.tasting_notes}</p>
              </section>
            )}

            {!recipe.is_vegan && (recipe.vegan_alternative || veganSwap) && (
              <section className="border border-lime-800/60 bg-lime-950/10 p-8 md:p-10" data-testid="vegan-alternative">
                <div className="flex items-center gap-3 mb-4">
                  <Sprout className="w-4 h-4 text-lime-500" />
                  <span className="eyebrow text-lime-500">Vegan-shaped, no compromise</span>
                </div>
                {recipe.vegan_alternative && (
                  <p className="text-stone-300 leading-relaxed text-lg">{recipe.vegan_alternative}</p>
                )}

                {veganSwap && (
                  <Link
                    to={`/recipe/${veganSwap.id}`}
                    data-testid="vegan-swap-card"
                    className={`group ${recipe.vegan_alternative ? "mt-6" : ""} flex items-stretch gap-5 border border-lime-800/60 bg-stone-950/60 hover:border-lime-500 hover:bg-lime-950/20 transition-colors`}
                  >
                    <div className="w-28 sm:w-36 shrink-0 bg-stone-900 border-r border-lime-900/40 aspect-square overflow-hidden">
                      {veganSwap.image_status === "ready" ? (
                        <img
                          src={`${BACKEND}/api/recipes/${veganSwap.id}/og_image`}
                          alt={veganSwap.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Sprout className="w-8 h-8 text-lime-800" strokeWidth={1.2} />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 py-4 pr-5 flex flex-col justify-center min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-lime-400" />
                        <span className="eyebrow text-lime-500 text-[10px]">
                          A proper vegan {veganSwap.course}
                        </span>
                      </div>
                      <div className="font-serif-display text-xl md:text-2xl text-stone-100 leading-tight truncate group-hover:text-lime-300 transition-colors">
                        {veganSwap.title}
                      </div>
                      {veganSwap.region && (
                        <div className="text-xs text-stone-500 mt-1.5 font-mono-accent uppercase tracking-widest truncate">
                          {veganSwap.region} · {veganSwap.season}
                        </div>
                      )}
                      <div className="text-[11px] text-lime-500 mt-2 uppercase tracking-[0.2em] opacity-0 group-hover:opacity-100 transition-opacity">
                        Open the full recipe →
                      </div>
                    </div>
                  </Link>
                )}

                {(recipe.vegan_alternative_nutrition ||
                  (recipe.vegan_alternative_allergens && recipe.vegan_alternative_allergens.length > 0)) && (
                  <div className="mt-6 pt-6 border-t border-lime-900/40" data-testid="vegan-nutrition-block">
                    {recipe.vegan_alternative_nutrition && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pb-4">
                        {[
                          { label: "Calories", val: recipe.vegan_alternative_nutrition.calories, unit: "kcal" },
                          { label: "Protein", val: recipe.vegan_alternative_nutrition.protein_g, unit: "g" },
                          { label: "Carbs", val: recipe.vegan_alternative_nutrition.carbs_g, unit: "g" },
                          { label: "Fat", val: recipe.vegan_alternative_nutrition.fat_g, unit: "g" },
                        ].map((n) => (
                          <div key={n.label} className="text-center" data-testid={`vegan-nutrition-${n.label.toLowerCase()}`}>
                            <div className="font-serif-display text-2xl md:text-3xl text-lime-400 leading-none">
                              {n.val != null ? Math.round(Number(n.val) * (servingsFactor || 1)) : "—"}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 mt-1.5">
                              {n.label} <span className="text-stone-600">{n.unit}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {recipe.vegan_alternative_allergens && recipe.vegan_alternative_allergens.length > 0 && (
                      <div className="flex flex-wrap items-center gap-2 pt-2">
                        <span className="eyebrow text-stone-500 mr-1">Contains</span>
                        {recipe.vegan_alternative_allergens.map((a) => (
                          <span
                            key={a}
                            data-testid={`vegan-allergen-${a}`}
                            className="text-[10px] uppercase tracking-widest px-2.5 py-1 bg-lime-950/40 border border-lime-800/60 text-lime-400"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="pt-3 text-[10px] uppercase tracking-widest text-stone-600 font-mono-accent">
                      Vegan version &middot; per serving
                    </div>
                  </div>
                )}
              </section>
            )}

            {recipe.pairings?.length > 0 && (
              <section>
                <div className="eyebrow mb-8 pt-8 border-t border-stone-800">What to Drink</div>
                <div className="grid md:grid-cols-3 gap-4">
                  {recipe.pairings.map((p, i) => {
                    const Icon = pairingIcon(p.type);
                    return (
                      <div key={i} data-testid={`pairing-${p.type}`} className="border border-stone-800 p-6 bg-stone-900/40">
                        <div className="flex items-center gap-2 mb-4">
                          <Icon className="w-4 h-4 text-orange-600" strokeWidth={1.5} />
                          <span className="eyebrow">{pairingLabel(p.type)}</span>
                        </div>
                        <div className="font-serif-display text-2xl text-stone-100 mb-3 leading-tight">{p.name}</div>
                        <p className="text-sm text-stone-400 leading-relaxed">{p.description}</p>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {recipe.chef_tips?.length > 0 && (
              <section>
                <div className="eyebrow mb-6 pt-8 border-t border-stone-800">A Few Things Worth Knowing</div>
                <ul className="space-y-4">
                  {recipe.chef_tips.map((tip, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="magazine-number text-2xl mt-1">&middot;</span>
                      <span className="font-serif-display italic text-xl text-stone-300 leading-relaxed">{renderTip(tip)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <CookPhotos recipeId={recipe.id} />
          </div>
        </div>
      </div>

      <SubstituteDialog
        open={subOpen}
        onOpenChange={setSubOpen}
        recipeId={recipe.id}
        ingredient={subIngredient}
      />
    </div>
  );
};

export default RecipeDetail;
